// ───────── Hero — animated 3D dot-grid wave ─────────────────────────────
//
// Three.js setup that paints a perspective grid of white dots into a full-
// viewport <canvas class="hero-wave"> sitting inside .hero-img. Each dot's
// Y position is computed in the vertex shader from overlapping sine
// waves, producing a sea-surface ripple. GPU does the per-vertex math so
// the CPU side is essentially free even at 16k dots.
//
// Replaces the earlier static-headshot / ASCII-art experiments at the
// user's request. The reference image showed a low-angle perspective of
// a dotted surface flowing like water — that's what's rendered here.
//
// GSAP scroll-blur / opacity-fade / parallax-Y rules in master.js still
// target the same parent layer (.hero-img). They've been retargeted from
// `.hero-ascii` to `.hero-wave` so the wave fades + blurs out as the
// user scrolls past the hero, exactly as the headshot photo did.
//
// Reduced-motion: renders one static frame and never starts the rAF
// loop. Out-of-viewport: skips render (keeps the clock advancing so the
// wave is mid-flow when the user scrolls back up). HMR-safe: disposes
// the renderer + cancels rAF on hot-reload.

import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  BufferGeometry,
  BufferAttribute,
  ShaderMaterial,
  Points,
  Color,
  Vector4,
} from 'three';

// ─── Ripple pool sizing ────────────────────────────────────────────────
//
// Eight active ripples at a time. New ones overwrite the oldest slot, so
// a fast cursor sweep produces a trail of ripples that each expand and
// decay independently. Lifetime + speed tune how far each ripple travels
// before it dies; spawn interval throttles how many ripples a continuous
// mousemove can spawn per second.
const MAX_RIPPLES = 8;
const RIPPLE_LIFETIME = 2.5;     // in uTime units (≈ 4.3 s of real time)
const RIPPLE_SPEED = 2.5;        // world units per uTime unit
const RIPPLE_SPAWN_INTERVAL = 0.25; // seconds between spawns from mousemove
                                    // History: 0.10 → 0.15 → 0.25 (each step
                                    // = fewer concurrent ripples on fast
                                    // sweeps; "very subtle" register).

// Theme-aware dot colour. Dot tone shifts toward the active background
// so the surface stays subtle on either theme instead of stark white
// (dark mode) or stark black (light mode).
//
//   dark theme  (bg #000) → 0x808080 — mid grey, reads as soft white
//   light theme (bg #fff) → 0xa6a6a6 — light grey, reads as soft black
//
// Both values were bumped ~20% toward "more visible" at the user's call
// (history: dark 0x6a→0x80; light 0xbc→0xa6). Combined with the alpha
// nudge in the fragment shader, dots are noticeably more present
// without losing the muted "background texture" register.
function readThemeColor() {
  const theme =
    document.documentElement.getAttribute('data-theme') || 'dark';
  return new Color(theme === 'light' ? 0xa6a6a6 : 0x808080);
}

export function setupHeroWave({ reducedMotion = false } = {}) {
  const container = document.querySelector('.hero-img');
  if (!container) return null;

  const canvas = container.querySelector('canvas.hero-wave');
  if (!canvas) return null;

  const scene = new Scene();

  // Camera positioned low + behind, looking slightly down — gives the
  // horizon-line composition from the reference image (wave fills lower
  // ~60% of frame, dark sky above).
  const camera = new PerspectiveCamera(
    45,
    window.innerWidth / window.innerHeight,
    0.1,
    100
  );
  // Camera pushed back to 8.5 (was 6.5) at the user's call — wave reads
  // smaller in frame, with more dark "sky" above. lookAt unchanged so the
  // horizon line stays mid-composition.
  camera.position.set(0, 1.6, 8.5);
  camera.lookAt(0, -0.3, 0);

  const renderer = new WebGLRenderer({
    canvas,
    alpha: true,        // transparent — site bg shows through
    antialias: true,
    powerPreference: 'low-power',
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(window.innerWidth, window.innerHeight, false);

  // Grid dimensions. Denser grid reads more like the reference's dot
  // halftone; sparser grid is cheaper. 140×90 = 12 600 dots is a
  // comfortable middle ground for laptops + phones.
  const COLS = 140;
  const ROWS = 90;
  const SIZE_X = 32;
  const SIZE_Z = 22;

  const positions = new Float32Array(COLS * ROWS * 3);
  for (let z = 0; z < ROWS; z++) {
    for (let x = 0; x < COLS; x++) {
      const i = (z * COLS + x) * 3;
      positions[i] = (x / (COLS - 1) - 0.5) * SIZE_X;
      positions[i + 1] = 0; // y — computed in vertex shader
      positions[i + 2] = (z / (ROWS - 1) - 0.5) * SIZE_Z;
    }
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));

  // Vertex shader: layer three sine waves of different frequencies and
  // speeds for an interference-pattern surface that doesn't loop too
  // visibly. Perspective-correct gl_PointSize so close dots are larger
  // than far dots — that's the visual cue that sells "3D surface."
  //
  // Fragment shader: render each point as a round dot (discard outside
  // the unit circle in gl_PointCoord space). Alpha fades with depth so
  // the back of the grid melts into the dark bg — atmospheric perspective
  // without an actual fog object.
  // Initial ripple pool — all slots start inactive (age > lifetime so
  // the shader's `continue` skips them). Each Vector4 is (x, z, age, _).
  const ripples = Array.from({ length: MAX_RIPPLES }, () =>
    new Vector4(0, 0, RIPPLE_LIFETIME + 10, 0)
  );

  const material = new ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uColor: { value: readThemeColor() },
      uPixelRatio: { value: renderer.getPixelRatio() },
      // Ripple pool. Each entry: (x, z, age, _). Mutated in place by
      // the rAF loop; Three.js re-uploads the array each render.
      uRipples: { value: ripples },
    },
    vertexShader: /* glsl */ `
      uniform float uTime;
      uniform float uPixelRatio;
      uniform vec4 uRipples[${MAX_RIPPLES}];
      varying float vDepth;

      void main() {
        vec3 pos = position;

        // Three overlapping waves — directional bias so the dominant motion
        // sweeps along +X (left → right), with a slower cross-current along
        // Z to keep it from looking 1-dimensional. uTime multipliers
        // bumped +20% on this pass (0.42→0.50, 0.30→0.36, 0.22→0.26).
        float w1 = sin(pos.x * 0.45 + uTime * 0.50) * 0.32;
        float w2 = sin(pos.z * 0.55 + uTime * 0.36) * 0.22;
        float w3 = sin((pos.x * 0.7 + pos.z * 0.5) + uTime * 0.26) * 0.20;
        float baseHeight = w1 + w2 + w3;

        // Cursor-driven ripples. Each entry in uRipples holds an origin
        // (x, z) and an age. For every still-alive ripple the wavefront
        // sits at (age x SPEED) world-units from the origin -- we add a
        // gaussian ring envelope centred on that radius so each vertex
        // gets a height pulse when the front passes over it. The result
        // is concentric rings expanding outward from each cursor sample,
        // riding on top of the ambient three-wave base.
        float rippleHeight = 0.0;
        for (int i = 0; i < ${MAX_RIPPLES}; i++) {
          vec4 ripple = uRipples[i];
          float age = ripple.z;
          if (age >= ${RIPPLE_LIFETIME.toFixed(2)}) continue;

          vec2 toRipple = vec2(pos.x, pos.z) - ripple.xy;
          float dist = length(toRipple);

          float front = age * ${RIPPLE_SPEED.toFixed(2)};
          float dr = dist - front;

          // Narrow gaussian ring centred on the expanding wavefront.
          // The 2.5 controls ring "thickness" — bigger = thinner ring.
          float ring = exp(-dr * dr * 2.5);

          // Linear fade across the ripple's lifetime — peak at age 0,
          // zero at age = LIFETIME.
          float fade = 1.0 - age / ${RIPPLE_LIFETIME.toFixed(2)};

          // Ripple peak amplitude trimmed for a "very subtle" register
          // (history: 0.4 → 0.2 → 0.08). At 0.08 each ring is barely a
          // nudge on the surface — readable as cursor influence on close
          // attention, invisible at a glance.
          rippleHeight += ring * fade * 0.08;
        }

        pos.y = baseHeight + rippleHeight;

        vec4 mvPos = modelViewMatrix * vec4(pos, 1.0);
        vDepth = -mvPos.z;

        // gl_PointSize scaled by inverse depth for true perspective dots.
        // 20.0 = "base dot size at unit depth" — bump for chunkier dots,
        // drop for finer. Trimmed another 60% on this pass (51 → 20);
        // history: 220 → 85 → 51 → 20. Reads as fine halftone from
        // the new pushed-back camera distance.
        gl_PointSize = 20.0 * uPixelRatio / vDepth;
        gl_Position = projectionMatrix * mvPos;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      varying float vDepth;

      void main() {
        // Round dot — discard outside the unit circle in point coords.
        vec2 uv = gl_PointCoord - 0.5;
        float d = length(uv);
        if (d > 0.5) discard;

        // Soft inner falloff — gives dots a subtle bloom edge.
        float edge = smoothstep(0.5, 0.35, d);

        // Atmospheric fade: dots further away (vDepth higher) get
        // dimmer so the horizon melts into the background.
        float depthFade = 1.0 - smoothstep(4.0, 18.0, vDepth);

        // Alpha multiplier bumped 0.95 → 1.0 at the user's call so dots
        // hit full opacity at the front of the field (still depth-faded
        // toward the horizon via depthFade).
        float alpha = edge * depthFade * 1.0;
        gl_FragColor = vec4(uColor, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
  });

  // Update dot colour when the theme toggle fires. Direct uniform write
  // — no shader rebuild, just the next render frame picks it up.
  function onThemeChange() {
    material.uniforms.uColor.value = readThemeColor();
  }
  document.addEventListener('themechange', onThemeChange);

  // ─── Ripple spawning ────────────────────────────────────────────────
  //
  // Each mousemove (throttled to RIPPLE_SPAWN_INTERVAL) drops a new
  // ripple at the cursor's projected position on the wave plane. New
  // ripples overwrite the oldest slot in the pool so the array stays
  // bounded; the shader culls dead slots via the age check.
  //
  // Cursor → wave-plane projection is the same coarse linear mapping
  // we used for the old static bump (NDC.x * 10, -NDC.y * 8) — good
  // enough for an aesthetic effect, no raycast needed.
  //
  // Listener is on window (not canvas) since .hero-img is
  // pointer-events:none + z-index:-1 — ripples respond to cursor
  // anywhere on the page, which feels right for a background canvas.
  let lastSpawnTime = -Infinity;

  function onPointerMove(e) {
    const nowSec = performance.now() / 1000;
    if (nowSec - lastSpawnTime < RIPPLE_SPAWN_INTERVAL) return;
    lastSpawnTime = nowSec;

    const ndcX = (e.clientX / window.innerWidth) * 2 - 1;
    const ndcY = -((e.clientY / window.innerHeight) * 2 - 1);
    const waveX = ndcX * 10.0;
    const waveZ = -ndcY * 8.0;

    // Find oldest ripple slot and overwrite it with this fresh one.
    let oldestIdx = 0;
    let oldestAge = -Infinity;
    for (let i = 0; i < MAX_RIPPLES; i++) {
      const age = ripples[i].z;
      if (age > oldestAge) {
        oldestAge = age;
        oldestIdx = i;
      }
    }
    ripples[oldestIdx].set(waveX, waveZ, 0, 0);
  }
  window.addEventListener('mousemove', onPointerMove, { passive: true });

  const points = new Points(geometry, material);
  scene.add(points);

  // ─── Resize ─────────────────────────────────────────────────────────
  function onResize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
    material.uniforms.uPixelRatio.value = renderer.getPixelRatio();
  }
  window.addEventListener('resize', onResize, { passive: true });

  // ─── Animate ────────────────────────────────────────────────────────
  let t = 0;
  let rafId = 0;

  function render() {
    material.uniforms.uTime.value = t;
    renderer.render(scene, camera);
  }

  // Reduced motion: one frame, no loop.
  if (reducedMotion) {
    render();
    return {
      dispose: () => {
        window.removeEventListener('resize', onResize);
        window.removeEventListener('mousemove', onPointerMove);
        document.removeEventListener('themechange', onThemeChange);
        geometry.dispose();
        material.dispose();
        renderer.dispose();
      },
    };
  }

  function loop() {
    rafId = requestAnimationFrame(loop);
    // t-step bumped +20% on this pass (0.008 → 0.0096) to compound with
    // the in-shader speed bump. Net wave motion ~20% faster than the
    // previous calm-sea baseline, still ~3× slower than the launch
    // version. History: 0.015 → 0.008 → 0.0096.
    const dt = 0.0096;
    t += dt;

    // Age every active ripple. Vector4 is (x, z, age, _) — bump z by dt.
    // Three.js sees the same uRipples reference and re-uploads the
    // mutated values each render.
    for (let i = 0; i < MAX_RIPPLES; i++) {
      ripples[i].z += dt;
    }

    // Skip render once hero is well off-screen — keep the clock
    // ticking so the wave is mid-flow when scrolled back to.
    if (window.scrollY > window.innerHeight * 0.85) return;

    render();
  }
  rafId = requestAnimationFrame(loop);

  return {
    dispose: () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mousemove', onPointerMove);
      document.removeEventListener('themechange', onThemeChange);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    },
  };
}
